"use client";

import { useState, useRef } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Package, Tag, Box, Weight, Ruler } from "lucide-react";
import { formatCurrency } from "@/utils/currency";
import { Badge } from "@/components/ui/badge";

import { Variant } from "@/types/models/product";

// Helper function to get variant display name from options
const getVariantDisplayName = (variant: any): string => {
  const options = [variant?.option1, variant?.option2, variant?.option3]
    .filter((opt) => opt && opt.trim() !== "");
  if (options.length > 0) {
    return options.join(" / ");
  }
  return variant?.title || "Unknown";
};

// Some API endpoints return variants in a slightly different shape (snake_case)
// Define a compatible type locally so components can accept either form.
type VariantApiShape = {
  id?: string;
  title?: string;
  sku?: string;
  inventory?: number | null;
  price?: number | null;
  listing_price?: number | null;
  listingPrice?: number | null;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
  compareAtPrice?: number | null;
  shippingDetails?: any;
};

interface ProductVariantsPopoverProps {
  variants: Array<Variant | VariantApiShape>;
  trigger: React.ReactNode;
}

export function ProductVariantsPopover({ variants, trigger }: ProductVariantsPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100); // Small delay to allow moving to popover
  };

  if (!variants || variants.length === 0) {
    return <>{trigger}</>;
  }

  const totalInventory = variants.reduce((sum, variant) => {
    const inv = (variant as any).inventory ?? (variant as any).inventory ?? 0;
    return sum + (typeof inv === "number" ? inv : 0);
  }, 0);

  const averagePrice =
    variants.reduce((sum, variant) => {
      const v = variant as any;
      const price = typeof v.price === "number" ? v.price : (typeof v.listing_price === "number" ? v.listing_price : (typeof v.listingPrice === "number" ? v.listingPrice : 0));
      return sum + price;
    }, 0) / Math.max(1, variants.length);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className="cursor-pointer"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-0 bg-card border border-border/40 shadow-lg"
        align="start"
        side="right"
        sideOffset={-20}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-border/40">
            <Package className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">
              Product Variants ({variants.length})
            </h3>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {variants.map((variant, index) => (
              <div
                key={variant.id || index}
                className="flex items-start gap-3 p-3 rounded-md hover:bg-muted/30 transition-colors border border-border/30"
              >
                {/* Variant Icon */}
                <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                  <Box className="h-4 w-4 text-muted-foreground" />
                </div>

                {/* Variant Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {getVariantDisplayName(variant)}
                  </h4>

                  {/* SKU */}
                  {variant.sku && (
                    <div className="flex items-center gap-1 mt-1">
                      <Tag className="h-3 w-3 text-gray-500" />
                      <span className="text-xs text-gray-600 font-mono">
                        {variant.sku}
                      </span>
                    </div>
                  )}

                  {/* Variant Options */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {variant.option1 && (
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/20 text-primary border-0 rounded-full">
                        {variant.option1}
                      </Badge>
                    )}
                    {variant.option2 && (
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/20 text-primary border-0 rounded-full">
                        {variant.option2}
                      </Badge>
                    )}
                    {variant.option3 && (
                      <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/20 text-primary border-0 rounded-full">
                        {variant.option3}
                      </Badge>
                    )}
                  </div>

                  {/* Inventory */}
                  <div className="flex items-center gap-1 mt-1">
                    <Package className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-600">
                      Stock: {typeof variant.inventory === 'number' ? variant.inventory : 0} units
                    </span>
                    {typeof variant.inventory === 'number' && variant.inventory < 10 && (
                      <Badge variant="destructive" className="text-xs ml-1">
                        Low Stock
                      </Badge>
                    )}
                  </div>

                  {/* Shipping Details */}
                  {variant.shippingDetails && (
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1">
                        <Weight className="h-3 w-3 text-gray-500" />
                        <span className="text-xs text-gray-600">
                          {variant.shippingDetails.weight}{variant.shippingDetails.weightUnit}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Ruler className="h-3 w-3 text-gray-500" />
                        <span className="text-xs text-gray-600">
                          {variant.shippingDetails.length}×{variant.shippingDetails.width}×{variant.shippingDetails.height}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-semibold text-foreground">
                    {formatCurrency(typeof variant.price === 'number' ? variant.price : (variant as any).listing_price ?? (variant as any).listingPrice ?? 0)}
                  </div>
                  {typeof variant.compareAtPrice === 'number' && typeof variant.price === 'number' && variant.compareAtPrice !== variant.price && (
                    <div className="text-xs text-gray-500 line-through">
                      {formatCurrency(variant.compareAtPrice)}
                    </div>
                  )}
                  {typeof variant.compareAtPrice === 'number' && typeof variant.price === 'number' && variant.compareAtPrice > variant.price && (
                    <div className="text-xs text-green-600">
                      {Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100)}% off
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-3 pt-3 border-t border-border/40">
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Total Variants:</span>
                <span className="text-xs font-medium text-foreground">
                  {variants.length}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Total Inventory:</span>
                <span className="text-xs font-medium text-foreground">
                  {totalInventory} units
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-foreground">
                  Avg. Price:
                </span>
                <span className="text-xs font-semibold text-foreground">
                  {formatCurrency(Math.round(averagePrice))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
